import React, { useState } from 'react';
import { Field, FieldArray, useFormikContext } from 'formik';
import { Box, Button } from 'lmc-ui-library';
import { FIELDS_LABELS } from '../types';
import { CommissionTermsProps } from './types';
import { produceNewTerm } from '../utils';
import ChangeableSelect from 'shared/Profile/ChangeableSelect';

import styles from '../profile.module.scss';
import { Icon } from '../../../modules/af_cm_ui_kit';
import { CommissionTermItem, TermSelectContainer, WarningMessage, WarningContainer } from './CommissionTerms.styled';

const CommissionTerms: React.FC<CommissionTermsProps> = ({
  title = 'Commission terms',
  singleItemTitle = FIELDS_LABELS.COMMISSION_TERM,
  canDeleteEveryItem = false,
  fieldName,
  commissionTerms,
  assignCommissionTerm,
  unassignCommissionTerm,
  shouldBeDisabled,
  showRequired = false,
  hasDuplicates = false,
  hasMissingCoverage = false,
  onUpdate,
}) => {
  const { values } = useFormikContext<any>();

  const [commissionValues, setCommissionValues] = useState(values[fieldName]);

  const updatePreviousValue = (index: number) => (value: string) => {
    setCommissionValues((prevState: string[]) => {
      const previousState = [...prevState];
      previousState[index] = value;

      return previousState;
    });
  };

  const handleAssign = (index: number, values: any) => async (data: any, previousValue?: any) => {
    const result = await assignCommissionTerm(index)(data, previousValue);
    if (result) {
      let updatedValues = [];
      if (Array.isArray(commissionValues)) {
        updatedValues = [...commissionValues.filter((v: string) => v), data];
      } else {
        updatedValues = [data];
      }
      setCommissionValues(updatedValues);
      if (onUpdate) {
        onUpdate();
      }
    }
  };

  return (
    <Box title={title} className={styles.box}>
      <Box.Content>
        <WarningContainer>
          {hasDuplicates && <WarningMessage>Some of the below Commission Terms cover the same Products</WarningMessage>}
          {hasMissingCoverage && <WarningMessage>Below Commission Terms don't cover all Products</WarningMessage>}
        </WarningContainer>
        <FieldArray
          name={fieldName}
          render={(arrayHelpers) => (
            <>
              {values[fieldName].map((term: string, index: number) => {
                const handleDelete = () => {
                  if (!window.confirm('Are you sure?')) {
                    return;
                  }
                  const filterDuplicates = values[fieldName].filter((val: string) => val === term);
                  if (term && filterDuplicates.length === 1) {
                    unassignCommissionTerm(term);
                  }

                  arrayHelpers.remove(index);

                  const updatedValues = [...commissionValues];
                  updatedValues.splice(index, 1);
                  setCommissionValues(updatedValues);
                };

                return (
                  <CommissionTermItem key={`${term}-${index}`}>
                    <TermSelectContainer>
                      <Field
                        name={`${fieldName}.${index}`}
                        title={`${singleItemTitle} ${index + 1}`}
                        component={ChangeableSelect}
                        options={commissionTerms}
                        onChangeClick={handleAssign(index, values)}
                        disabled={shouldBeDisabled}
                        previousValue={commissionValues[index]}
                        updatePreviousValue={updatePreviousValue(index)}
                        afterSaveButton={() => (
                          <>
                            {(canDeleteEveryItem || values[fieldName].length > 1) && (
                              <Button
                                style={{
                                  marginLeft: '12px',
                                  paddingTop: '12px',
                                  paddingBottom: '12px',
                                }}
                                mode="ghost"
                                borderVariant="rectangle"
                                type="button"
                                onClick={handleDelete}
                                disabled={shouldBeDisabled}>
                                <Icon type="trash" />
                              </Button>
                            )}
                          </>
                        )}
                        isRequired={index === 0 && showRequired}
                      />
                    </TermSelectContainer>
                  </CommissionTermItem>
                );
              })}
              <Button
                mode="ghost"
                borderVariant="rectangle"
                style={{ minWidth: 100, marginRight: 16 }}
                type="button"
                onClick={() => arrayHelpers.push(produceNewTerm())}
                disabled={shouldBeDisabled}>
                Add another commission
              </Button>
            </>
          )}
        />
      </Box.Content>
    </Box>
  );
};

export default CommissionTerms;
